import { FocalPoint } from './Picture';

export const computeFocalObjectPosition = (focal: FocalPoint, imgW: number, imgH: number, boxW: number, boxH: number): string => {
	if (!focal || !imgW || !imgH || !boxW || !boxH) return '50% 50%';

	const computeObjectPositionFromFocal = (focal: FocalPoint, imgW: number, imgH: number, boxW: number, boxH: number): string => {
		// If any of the dimensions are invalid or zero, fallback to a simple
		// percentage based on the focal point.
		if (boxW <= 0 || boxH <= 0 || imgW <= 0 || imgH <= 0) {
			return `${focal.left * 100}% ${focal.top * 100}%`;
		}

		// Calculate the scale for "cover":
		// We scale the image so it completely fills the container.
		// Use the larger ratio of container/image width or height to ensure no empty space.
		const scale = Math.max(boxW / imgW, boxH / imgH);

		// Dimensions of the image after scaling to cover the container.
		const renderedW = imgW * scale;
		const renderedH = imgH * scale;

		/**
		 * Helper function to calculate object-position along one axis (x or y)
		 * so that the focal point is centered in the container.
		 *
		 * @param focalCoord - normalized focal coordinate (0..1) along the axis
		 * @param renderedSize - size of the image along this axis after scaling
		 * @param boxSize - size of the container along this axis
		 * @returns position percentage for CSS object-position
		 */
		const axis = (focalCoord: number, renderedSize: number, boxSize: number): number => {
			// Difference between container size and scaled image size (negative if cropped)
			const denom = boxSize - renderedSize;

			// If the image fits perfectly along this axis, return simple percentage
			if (Math.abs(denom) < 1e-6) return focalCoord * 100;

			// Compute offset so that the focal point aligns with the container center
			const pos = (boxSize / 2 - focalCoord * renderedSize) / denom;

			// Convert to percentage for CSS
			return pos * 100;
		};

		// Calculate horizontal and vertical object-position
		let x = axis(focal.left, renderedW, boxW);
		let y = axis(focal.top, renderedH, boxH);

		// Clamp values to valid CSS percentages (0% to 100%)
		x = Math.max(0, Math.min(100, x));
		y = Math.max(0, Math.min(100, y));

		// Return as a string suitable for the object-position CSS property
		return `${x}% ${y}%`;
	};
	return computeObjectPositionFromFocal(focal, imgW, imgH, boxW, boxH);
};
